import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { TrashIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { black } from 'tailwindcss/colors';

const Poster = () => {
  const [image, setImage] = useState(null);
  const [url, setUrl] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  // Fetch posters from the API
  const fetchPosters = async () => {
    try {
      const response = await axios.get('https://visitguru.in/api/v1/posters/all');
      if (response.data.success) {
        setTableData(response.data.data); // Already sorted by backend
      } else {
        toast.error('Failed to fetch posters');
      }
    } catch (err) {
      console.error('Failed to fetch posters:', err.message);
      toast.error('Failed to fetch posters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosters();
  }, []);

  // Handle image selection
  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  // Handle form submission to add a new poster
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!image || !url) {
      toast.warning('Please upload an image and enter a URL');
      return;
    }

    const formData = new FormData();
    formData.append('image', image);
    formData.append('link', url);

    try {
      await axios.post('https://visitguru.in/api/v1/posters/add', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await fetchPosters();
      setImage(null);
      setUrl('');
      toast.success('Image uploaded successfully');
      if (fileInputRef.current) fileInputRef.current.value = null;
    } catch (error) {
      console.error('Upload failed:', error.message);
      toast.error('Failed to upload. Please try again.');
    }
  };

  // Handle poster deletion
  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://visitguru.in/api/v1/posters/delete/${id}`);
      await fetchPosters();
      toast.success('Deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error.message);
      toast.error('Failed to delete. Please try again.');
    }
  };

  // Handle drag-and-drop reordering
  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;

    const newData = Array.from(tableData);
    const [reorderedItem] = newData.splice(result.source.index, 1);
    newData.splice(result.destination.index, 0, reorderedItem);

    setTableData(newData);

    const order = newData.map((item) => item._id);

    try {
      const response = await axios.post('https://visitguru.in/api/v1/posters/reorder', { order });
      if (response.data.success) {
        setTableData(response.data.data); // Always use backend sorted list
        toast.success('Posters reordered successfully');
      } else {
        toast.error('Failed to reorder posters: ' + response.data.message);
        await fetchPosters();
      }
    } catch (error) {
      console.error('Reorder failed:', error.message);
      toast.error('Failed to reorder posters');
      await fetchPosters();
    }
  };

  return (
    <div className="space-y-12">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
        <div className="rounded-t px-4 py-3 border-0 bg-blue-600 text-white">
          <h2 className="text-lg font-semibold" style={{ color: black }}>
            Poster Upload
          </h2>
        </div>

        {/* Upload Form */}
        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="flex flex-col md:flex-row items-center gap-x-4 w-full">
            <div className="flex flex-col w-full md:w-1/3 m-2">
              <label className="text-sm font-medium text-gray-700 mb-1">Select Image</label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageChange}
                className="border border-gray-300 rounded px-3 py-2"
              />
            </div>

            <div className="flex flex-col w-full md:w-1/2">
              <label className="text-sm font-medium text-gray-700 mb-1">Enter URL</label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                className="border border-gray-300 rounded px-3 py-2"
              />
            </div>

            <div className="flex items-end pt-6">
              <button
                type="submit"
                style={{
                  backgroundColor: '#0884c4',
                  width: '120px',
                  height: '42px',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  marginLeft: '10px',
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </form>

        {/* Draggable Table */}
        <div className="overflow-x-auto px-4 pb-6">
          {loading ? (
            <div>Loading...</div>
          ) : (
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="posters">
                {(provided) => (
                  <table
                    className="w-full table-auto border rounded mt-4"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <thead className="bg-gray-100 text-gray-700 text-left">
                      <tr>
                        <th className="px-4 py-2 border">Drag</th>
                        <th className="px-4 py-2 border">#</th>
                        <th className="px-4 py-2 border">Image</th>
                        <th className="px-4 py-2 border">URL</th>
                        <th className="px-4 py-2 border">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.length > 0 ? (
                        tableData.map((item, index) => (
                          <Draggable key={item._id} draggableId={item._id} index={index}>
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="text-left border-b hover:bg-gray-50"
                              >
                                <td className="px-4 py-2 border" {...provided.dragHandleProps}>
                                  <svg
                                    className="w-5 h-5 text-gray-500 cursor-move"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M4 8h16M4 16h16"
                                    />
                                  </svg>
                                </td>
                                <td className="px-4 py-2 border">{index + 1}</td>
                                <td className="px-4 py-2 border">
                                  {/* FIXED: Use full Cloudinary URL directly */}
                                  <img
                                    src={item.image}
                                    alt="poster"
                                    className="h-10 mx-auto object-contain"
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
                                    }}
                                  />
                                </td>
                                <td className="px-4 py-2 border break-all">
                                  <a
                                    href={item.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline"
                                  >
                                    {item.link}
                                  </a>
                                </td>
                                <td className="px-4 py-2 border">
                                  <button
                                    onClick={() => handleDelete(item._id)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <TrashIcon className="w-5 h-5 inline" />
                                  </button>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-gray-500">
                            No records found.
                          </td>
                        </tr>
                      )}
                      {provided.placeholder}
                    </tbody>
                  </table>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </div>
    </div>
  );
};

export default Poster;